import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Ventures.css';

import heroImg from '../assets/YOCILAB Webiste photos/YOCILAB Ventures/Ventures Home.jpg';
import venturesImg from '../assets/YOCILAB Webiste photos/YOCILAB Ventures/YOCILAB Ventures 1.jpg';
import cohortImg from '../assets/YOCILAB Webiste photos/YOCILAB Ventures/DSC_9522.jpg';

const programSteps = [
  {
    step: '01',
    title: 'Apply & Selection',
    desc: 'Submit your idea, prototype, or early venture. Our team reviews applications and selects the most promising teams for each cohort through a structured assessment process.',
  },
  {
    step: '02',
    title: 'Cohort Kickoff',
    desc: 'Selected teams onboard into a structured 3–6 month program. You receive a dedicated workspace, program materials, and introductions to your mentor network.',
  },
  {
    step: '03',
    title: 'Build & Validate',
    desc: 'With weekly check-ins, expert workshops, and hands-on mentor guidance, you validate your business model, build your product, and understand your market.',
  },
  {
    step: '04',
    title: 'Seed Capital & Partnerships',
    desc: 'High-performing ventures access seed grants, introductions to angel investors, and connections to strategic partners who can accelerate growth.',
  },
  {
    step: '05',
    title: 'Pitch Day & Demo',
    desc: 'Each cohort closes with a public Pitch Day where founders present to investors, development organizations, corporate partners, and the YOCILAB community.',
  },
  {
    step: '06',
    title: 'Alumni Network & Continued Support',
    desc: 'After the program, ventures join the YOCILAB Alumni network — retaining access to mentors, community events, future funding rounds, and partner introductions.',
  },
];

const whatYouGet = [
  { label: 'Workspace Access', detail: '3–6 months of dedicated co-working space at YOCILAB Creative Space' },
  { label: 'Structured Curriculum', detail: 'Business modeling, customer discovery, financial planning, legal incorporation, and pitch training' },
  { label: 'Expert Mentorship', detail: '1-on-1 sessions with sector-specific mentors drawn from business, tech, and development sectors' },
  { label: 'Seed Funding Access', detail: 'Direct seed grants and introductions to early-stage investors and angel networks' },
  { label: 'Strategic Partnerships', detail: 'Connections to corporate sponsors, development organizations, and market access partners' },
  { label: 'Pitch Day Platform', detail: 'A public demo day to showcase your venture to investors, media, and community stakeholders' },
  { label: 'YOCILAB Network', detail: 'Lifetime access to the YOCILAB ventures community, alumni events, and peer founder circles' },
  { label: 'Media & Visibility', detail: 'Dedicated content and social media coverage of your venture journey and milestones' },
];

const focusSectors = [
  'EdTech & Skills Development', 'AgriTech & Food Security', 'FinTech & Financial Inclusion',
  'HealthTech & Community Wellbeing', 'Clean Energy & Climate', 'Creative Economy & Media',
  'Youth Employability Platforms', 'Community-Led Social Enterprise',
];

const Ventures = () => (
  <div className="ventures-page">
    <SEO
      title="YOCILAB Ventures | Incubator & Accelerator"
      description="YOCILAB Ventures is a youth-focused startup incubator and accelerator supporting young African founders with workspace, mentorship, seed funding, and investor access."
    />

    {/* HERO */}
    <section className="ventures-hero">
      <div className="ventures-hero__bg">
        <img src={heroImg} alt="YOCILAB Ventures" className="ventures-hero__img" />
        <div className="ventures-hero__overlay" />
      </div>
      <div className="container">
        <div className="ventures-hero__badge">
          <span className="ventures-hero__badge-dot" />
          INCUBATOR & ACCELERATOR PROGRAM
        </div>
        <h1 className="ventures-hero__title">
          From Idea to<br />
          <em>Investable Venture</em>
        </h1>
        <p className="ventures-hero__subtitle">
          A structured 3–6 month program helping young African founders validate ideas, build products, and access seed capital.
        </p>
      </div>
    </section>

    {/* OVERVIEW */}
    <section className="ventures-overview-section">
      <div className="container">
        <div className="ventures-overview-grid">
          <div className="ventures-overview-text">
            <span className="section-pill">ABOUT THE PROGRAM</span>
            <h2>Built for builders who are solving real problems.</h2>
            <p>
              Across Africa, talented young people have the ideas, the energy, and the will to build — but lack the structured support, capital, and networks to turn ambition into impact. YOCILAB Ventures closes that gap.
            </p>
            <p>
              We select high-potential founders and early-stage teams each cohort and surround them with the tools, mentorship, and funding access they need to build ventures that are both commercially sustainable and socially meaningful.
            </p>
            <p>
              Our focus is intentionally broad — we invest in founders solving challenges in education, agriculture, health, finance, energy, and community development, with a strong bias toward solutions designed for underserved African communities.
            </p>
          </div>
          <div className="ventures-overview-visual">
            <img src={venturesImg} alt="YOCILAB Ventures in action" className="ventures-overview-img" loading="lazy" />
            <div className="ventures-stat-strip">
              <div className="ventures-stat">
                <span className="ventures-stat__value">80+</span>
                <span className="ventures-stat__label">Ventures Incubated</span>
              </div>
              <div className="ventures-stat">
                <span className="ventures-stat__value">$50K+</span>
                <span className="ventures-stat__label">Seed Capital Deployed</span>
              </div>
              <div className="ventures-stat">
                <span className="ventures-stat__value">15+</span>
                <span className="ventures-stat__label">Institutional Partners</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* PROGRAM JOURNEY */}
    <section className="ventures-journey-section">
      <div className="container">
        <div className="ventures-section-header">
          <span className="section-pill">THE PROGRAM JOURNEY</span>
          <h2>From application to alumni.</h2>
          <p>A structured 6-step pathway designed to take your venture from early concept to investor-ready.</p>
        </div>
        <div className="ventures-steps">
          {programSteps.map((s) => (
            <div key={s.step} className="ventures-step">
              <span className="ventures-step__num">{s.step}</span>
              <div className="ventures-step__body">
                <h3 className="ventures-step__title">{s.title}</h3>
                <p className="ventures-step__desc">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* WHAT YOU GET */}
    <section className="ventures-benefits-section">
      <div className="container">
        <div className="ventures-benefits-grid">
          <div className="ventures-benefits-text">
            <span className="section-pill">WHAT YOU GET</span>
            <h2>Everything a founder needs to build and grow.</h2>
            <p>
              The program is designed to remove the most common blockers young African founders face — space, mentorship, capital, and networks — all within one structured support system.
            </p>
            <Link to="/contact" className="ventures-cta-link">Apply for the next cohort →</Link>
          </div>
          <div className="ventures-benefits-list">
            {whatYouGet.map((item, i) => (
              <div key={i} className="ventures-benefit-item">
                <div className="ventures-benefit-item__icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div>
                  <strong>{item.label}</strong>
                  <p>{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* FOCUS SECTORS */}
    <section className="ventures-sectors-section">
      <div className="container">
        <div className="ventures-section-header">
          <span className="section-pill">FOCUS SECTORS</span>
          <h2>Where we invest our energy and resources.</h2>
          <p>We prioritize ventures addressing systemic challenges across these key sectors in African communities.</p>
        </div>
        <div className="ventures-sectors-grid">
          {focusSectors.map((sector) => (
            <div key={sector} className="ventures-sector-chip">
              <span className="ventures-sector-chip__dot" />
              {sector}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* COHORT PHOTO + CTA */}
    <section className="ventures-cta-section">
      <div className="container">
        <div className="ventures-cta-block">
          <img src={cohortImg} alt="Ventures cohort" className="ventures-cta-img" loading="lazy" />
          <div className="ventures-cta-content">
            <span className="ventures-cta-eyebrow">APPLY FOR THE NEXT COHORT</span>
            <h2>Is your idea ready to be built?</h2>
            <p>
              We select ambitious founders with a strong problem–solution fit and the drive to build ventures that matter. If that sounds like you, apply now.
            </p>
            <div className="ventures-cta-btns">
              <Link to="/contact" className="btn btn-accent">Apply Now</Link>
              <Link to="/contact" className="btn btn-outline-white">Mentor or Sponsor a Cohort</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Ventures;
